import React, { Suspense, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { NavigationRail } from './NavigationRail'
import { MobileMenuButton } from './MobileMenuButton'
import { TabConfig, NavigationItem } from '../types'
import { VoipLauncher } from '@/core/voip'

interface MainLayoutProps {
  tabs: TabConfig[]
  navigationItems?: NavigationItem[]  // New: grouped navigation structure
  children?: React.ReactNode
  viewComponents?: Record<string, React.ComponentType<any>>
  defaultComponent?: React.ComponentType<any>
  onNavigationChange?: (view: string, params?: any) => void
  getBadgeValue?: (tabId: string) => string | number | undefined
}

export const MainLayout: React.FC<MainLayoutProps> = ({
  tabs,
  navigationItems,
  children,
  viewComponents = {},
  defaultComponent,
  onNavigationChange,
  getBadgeValue
}) => {
  const [isRailCollapsed, setIsRailCollapsed] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const location = useLocation()

  // Determine current view from location
  const getCurrentView = () => {
    const currentTab = tabs.find(tab => location.pathname.startsWith(tab.path))
    return currentTab?.id || ''
  }

  const renderContent = () => {
    if (children) return children

    const currentView = getCurrentView()
    const ViewComponent = viewComponents[currentView]

    if (ViewComponent) {
      return <ViewComponent onNavigationChange={onNavigationChange} />
    }

    if (defaultComponent) {
      const DefaultComponent = defaultComponent
      return <DefaultComponent onNavigationChange={onNavigationChange} />
    }

    return <Outlet />
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Navigation Rail */}
      <NavigationRail
        tabs={tabs}
        navigationItems={navigationItems}
        onCollapse={setIsRailCollapsed}
        isCollapsed={isRailCollapsed}
        mobileOpen={isMobileMenuOpen}
        onMobileToggle={setIsMobileMenuOpen}
        getBadgeValue={getBadgeValue}
      />
      
      {/* Mobile Menu Button */}
      <MobileMenuButton 
        onClick={() => setIsMobileMenuOpen(true)}
      />
      
      {/* Main Content Area */}
      <main className={`
        shared-main-content flex-1 overflow-auto transition-all duration-300 ease-in-out
        ${isRailCollapsed ? 'rail-collapsed' : ''}
      `}
        style={{
          marginLeft: isRailCollapsed ? '70px' : '240px',
          width: isRailCollapsed ? 'calc(100% - 70px)' : 'calc(100% - 240px)',
          background: '#f5f6fa'
        }}>
        <Suspense fallback={
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500">Loading...</div>
          </div>
        }>
          {renderContent()}
        </Suspense>
      </main>

      {/* Embeddable softphone — floating launcher in the bottom-right.
          Renders only for users whose role is 'admin' or 'bureau';
          a no-op for everyone else. */}
      <VoipLauncher />
    </div>
  )
}